import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { OwnerLoginInput } from "@steelbill/types";
import { LoginForm } from "@/components/login-form";
import { useAuth } from "@/features/auth/auth-context";
import { ApiError } from "@/lib/api-error";
import { routes } from "@/routes/paths";

/**
 * The sign-in page — `login-04`'s page wrapper, verbatim.
 *
 * The form owns its own field state through react-hook-form; this screen owns
 * only what react-hook-form cannot know about — whether a request is in flight,
 * and what the server said if it refused.
 */
export function LoginScreen(): React.JSX.Element {
  const { t } = useTranslation();
  const { loginAsOwner, staySignedIn, setStaySignedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  /**
   * Where the session expired, so a re-signed-in user lands back where he was
   * rather than on Home. `lib/api.ts` sets `state.from` when a token refresh
   * fails mid-request.
   */
  const from = (location.state as { from?: string } | null)?.from ?? routes.home;

  async function submit(values: OwnerLoginInput): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const user = await loginAsOwner({ phone: values.phone.trim(), pin: values.pin }, staySignedIn);
      /*
       * The one place a toast is right on this screen. It reports something that
       * SUCCEEDED and then gets out of the way — which is what a toast is for,
       * and why the failure path below uses an `Alert` instead (§11.1: the owner
       * must still be able to read what went wrong a minute later; a toast is
       * gone on a timer).
       *
       * Fired BEFORE `navigate`, deliberately. Sonner lives above the router in
       * `App.tsx`, so the toast survives the transition and greets him on the
       * screen he lands on rather than flashing on the one he is leaving.
       */
      toast.success(t("auth.welcomeBack", { name: user.displayName }));
      navigate(from, { replace: true });
    } catch (caught) {
      /*
       * The server's own sentence when it has one — it is the only thing that
       * can say "this account is locked for 15 minutes", and §11.1 asks for
       * errors in plain language.
       *
       * The API deliberately returns ONE message for "no such phone" and "wrong
       * PIN", so this screen cannot be used to discover which numbers exist.
       */
      setError(caught instanceof ApiError ? caught.message : t("err.somethingWentWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-muted p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <LoginForm
          staySignedIn={staySignedIn}
          onStayChange={setStaySignedIn}
          busy={busy}
          error={error}
          onSubmit={(values) => void submit(values)}
        />
      </div>
    </div>
  );
}
